import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { smoothScrollTo } from "../lib/motion";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_254b404b-7873-4515-bdac-426c3efec3f4/artifacts/x61qgwzc_o2o%20logo%20-%20Copy.png";

const MANIFESTO_URL =
    "https://oceantooasis.substack.com/p/the-oceantooasis-manifesto";

const sectionLinks = [
    { label: "Home", id: "home" },
    { label: "Products", id: "products" },
    { label: "Technology", id: "technology" },
    { label: "Team", id: "team" },
    { label: "Contact", id: "contact" },
];

const getLinkClass = (isActive, scrolled) => {
    if (isActive) {
        return scrolled ? "bg-ocean text-white" : "bg-white/15 text-white";
    }
    return scrolled
        ? "text-ocean/80 hover:text-ocean hover:bg-white/60"
        : "text-white/85 hover:text-white hover:bg-white/10";
};

const MobileMenu = ({ sectionLinks, goToSection, setOpen }) => (
    <div
        className="md:hidden bg-lavender/95 backdrop-blur-xl border border-white/60 shadow-[0_12px_40px_rgba(15,42,68,0.15)] mx-4 mb-4 rounded-2xl p-4 flex flex-col gap-1"
        data-testid="nav-mobile-menu"
    >
        {sectionLinks.map((l) => (
            <button
                key={l.id}
                onClick={() => goToSection(l.id)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-left px-4 py-3 rounded-xl hover:bg-white/60 text-ocean font-medium transition"
            >
                {l.label}
            </button>
        ))}
        <Link
            to="/consulting"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-link-consulting"
            className="text-left px-4 py-3 rounded-xl hover:bg-white/60 text-ocean font-medium transition"
        >
            Consulting
        </Link>
        <a
            href={MANIFESTO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-link-manifesto"
            className="text-left px-4 py-3 rounded-xl hover:bg-white/60 text-ocean font-medium transition"
        >
            Manifesto
        </a>
        <a
            href="mailto:info@oceantooasis.in"
            className="mt-2 text-center px-5 py-3 rounded-xl bg-ocean text-white font-semibold"
        >
            Get in touch
        </a>
    </div>
);

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState("home");
    const location = useLocation();
    const navigate = useNavigate();

    const isHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 24);
            if (!isHome) return;
            let current = sectionLinks[0].id;
            for (const l of sectionLinks) {
                const el = document.getElementById(l.id);
                if (el && el.getBoundingClientRect().top <= 120) current = l.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
        // sectionLinks is a module-level constant; setScrolled/setActive are stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHome]);

    const goToSection = (id) => {
        if (isHome) {
            smoothScrollTo(id);
        } else {
            navigate(`/#${id}`);
        }
        setOpen(false);
    };

    const goHomeRoot = () => {
        if (isHome) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate("/");
        }
        setOpen(false);
    };

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-lavender/80 backdrop-blur-xl border-b border-white/60 shadow-[0_4px_24px_rgba(15,42,68,0.05)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <button
                    onClick={goHomeRoot}
                    className="flex items-center gap-3 group"
                    data-testid="nav-logo"
                >
                    <img
                        src={LOGO_URL}
                        alt="OceanToOasis"
                        className="h-9 w-9 object-contain transition-transform duration-500 group-hover:rotate-12"
                    />
                    <span
                        className={`font-display font-black tracking-tight text-lg hidden sm:block transition-colors duration-300 ${
                            scrolled ? "text-ocean" : "text-white"
                        }`}
                    >
                        Ocean
                        <span
                            className={
                                scrolled ? "text-teal" : "text-energy"
                            }
                        >
                            To
                        </span>
                        Oasis
                    </span>
                </button>

                <nav className="hidden md:flex items-center gap-1">
                    {sectionLinks.map((l) => {
                        const isActive = isHome && active === l.id;
                        return (
                            <button
                                key={l.id}
                                onClick={() => goToSection(l.id)}
                                data-testid={`nav-link-${l.id}`}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${getLinkClass(isActive, scrolled)}`}
                            >
                                {l.label}
                            </button>
                        );
                    })}
                    <Link
                        to="/consulting"
                        data-testid="nav-link-consulting"
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${getLinkClass(location.pathname === "/consulting", scrolled)}`}
                    >
                        Consulting
                    </Link>
                    <a
                        href={MANIFESTO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-link-manifesto"
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${getLinkClass(false, scrolled)}`}
                    >
                        Manifesto
                    </a>
                    <a
                        href="mailto:info@oceantooasis.in"
                        data-testid="nav-cta"
                        className="ml-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ocean text-white text-sm font-semibold hover:bg-ocean-mid transition-all duration-300 shadow-[0_6px_24px_rgba(15,42,68,0.2)] hover:-translate-y-0.5"
                    >
                        Get in touch
                    </a>
                </nav>

                <button
                    className="md:hidden p-2 rounded-full bg-white/50 border border-white/70"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {open && (
                <MobileMenu
                    sectionLinks={sectionLinks}
                    goToSection={goToSection}
                    setOpen={setOpen}
                />
            )}
        </header>
    );
};

export default Nav;
